import { useEffect, useState } from "react";
import { ApiReferenceReact } from "@scalar/api-reference-react";
import "@scalar/api-reference-react/style.css";
import DevDocsShell from "@/components/DevDocsShell";
import { useAuth } from "@/contexts/AuthContext";
import { http } from "@/lib/api";

/**
 * Scalar-powered API reference. Mounts at /developers/reference and reads the
 * spec from /api/v1/openapi.json. When the user is signed in and has an active
 * API key, we prefill it in Scalar's "Try it" console; otherwise the user
 * types a key into Scalar's own Bearer-token input.
 */
export default function DevReference() {
  const { user } = useAuth();
  const [prefillSecret, setPrefillSecret] = useState("");

  useEffect(() => {
    // Signed-out users just see the Scalar reference without any prefill.
    if (!user) return;
    // Fetch keys list; if an active key exists we remember its prefix. The
    // full secret is never retrievable from the backend (SHA-256 hashed), so
    // we can't literally prefill it. We leave the Bearer field empty but show
    // a note above Scalar reminding the user to paste their secret.
    http.get("/api-keys").then(({ data }) => {
      const active = (data?.keys || []).find((k) => k.active);
      if (active) setPrefillSecret(active.prefix); // only the prefix is known
    }).catch(() => {});
  }, [user]);

  const specUrl = `${window.location.origin}/api/v1/openapi.json`;

  return (
    <DevDocsShell fullBleed>
      <div data-testid="developers-reference-page" className="pb-10">
        {prefillSecret && (
          <div className="rounded-sm border border-amber-200 bg-amber-50 text-amber-900 text-xs px-3 py-2 mb-3 font-mono">
            You have an active key with prefix <b>{prefillSecret}</b>. Paste its full secret into
            Scalar's "Authentication" panel on the right to use the "Try it" console.
          </div>
        )}
        <div className="rounded-sm border border-ink-100 bg-white overflow-hidden">
          <ApiReferenceReact
            configuration={{
              url: specUrl,
              theme: "default",
              layout: "modern",
              hideDownloadButton: false,
              hideModels: false,
              darkMode: false,
              defaultHttpClient: {
                targetKey: "shell",
                clientKey: "curl",
              },
              authentication: {
                preferredSecurityScheme: "BearerAuth",
                http: {
                  bearer: {
                    token: "",
                  },
                },
              },
              metaData: {
                title: "LeadIQ Pro Developer API — Reference",
                description:
                  "Interactive reference for the /api/v1/* surface. Create a key in Settings → API to run live requests.",
              },
              customCss: `
                :root {
                  --scalar-color-accent: #002FA7;
                  --scalar-color-accent-dark: #001F6B;
                  --scalar-font: ui-sans-serif, system-ui, sans-serif;
                  --scalar-font-code: ui-monospace, "SF Mono", Menlo, monospace;
                  --scalar-radius: 2px;
                  --scalar-radius-lg: 4px;
                }
                .scalar-api-reference, .scalar-api-reference * {
                  font-feature-settings: "ss01" on;
                }
              `,
            }}
          />
        </div>
      </div>
    </DevDocsShell>
  );
}
